<template>
	<div class="video-player" :class="{ 'video-player--backdrop': isBackdrop }">
		<div class="video-player__play-button--container" v-show="!isActive" @click="$emit('play')">
			<slot name="play-btn">
				<div
					class="video-player__play-button"
					:class="{ 'video-player__play-button--backdrop': isBackdrop }"
					:style="{ backgroundImage: isDisneyPlusOffer ? 'none' : playIcon }"
				/>
			</slot>
		</div>

		<div
			v-show="!isActive || isBackdrop"
			class="video-player__image-preview-container"
			:class="{
				'video-player__image-preview-container--backdrop': isActive && isBackdrop,
				'video-player__image-preview-container--title-trailer': isDMTitleTrailer,
			}"
		>
			<slot name="image-preview"></slot>
		</div>

		<div
			v-show="isActive"
			class="video-player__video-container"
			:class="{
				'video-player__video-container--backdrop': isBackdrop,
				'video-player__video-container--title-trailer': isDMTitleTrailer,
			}"
		>
			<slot name="video-player"></slot>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

import { PlayerIcon } from '@/enums/player-icon';

const props = withDefaults(
	defineProps<{
		isActive?: boolean;
		isBackdrop?: boolean;
		playIconType?: PlayerIcon | null;
		isDisneyPlusOffer?: boolean;
		isDMTitleTrailer?: boolean;
	}>(),
	{
		isActive: false,
		isBackdrop: false,
		playIconType: null,
		isDisneyPlusOffer: false,
	}
);

const formattedPlayIconType = computed<PlayerIcon>(() => {
	if (props.playIconType) {
		return props.playIconType;
	}
	return props.isBackdrop ? PlayerIcon.JUSTWATCH : PlayerIcon.YOUTUBE;
});

const playIcon = computed(() => {
	const extension = formattedPlayIconType.value === PlayerIcon.YOUTUBE ? 'png' : 'svg';
	return `url(/${ASSETS_DIR}/img/${formattedPlayIconType.value}-icon-play.${extension})`;
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.video-player {
	overflow: hidden;
	border-radius: $jw-title-poster-radius;
	position: relative;
	max-height: 360px;

	&--backdrop {
		max-height: 450px;
		border-radius: 0;
	}

	&__image-preview-container {
		img {
			width: 100%;
		}

		&--backdrop {
			position: absolute;
			z-index: 1;
			width: 100%;
			opacity: 0.7;
			filter: blur(2px);
			transition: opacity 0.5s, filter 0.5s;

			img {
				object-fit: cover;
			}
		}

		&--title-trailer {
			img {
				object-fit: cover;
				aspect-ratio: 16 / 9;
			}
		}
	}

	&__video-container {
		&--backdrop {
			max-width: 1120px; // coming from max content width
			margin: 0 auto;
			z-index: 10;
			position: relative;
			//padding-bottom: 75px;
			display: block;
			height: 100%;
			@media (max-width: $screen-sm) {
				padding: 0;
			}
		}

		&--title-trailer {
			position: relative;
			height: 360px;
			overflow: hidden;
			@media (max-width: $screen-sm) {
				height: 172px;
			}
		}

		iframe {
			// force width and height for DailyMotion which has poor consistency on videos and trailers
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			border: none;
		}
	}

	&__play-button {
		position: absolute;
		height: 100%;
		width: 100%;
		background-size: 60px;
		background-position: center 45%;
		background-repeat: no-repeat;
		cursor: pointer;

		&--backdrop {
			@media (max-width: $screen-md) and (min-width: $screen-sm) {
				background-position: center 25%;
			}
			@media (max-width: $screen-sm) and (min-width: $screen-xs) {
				background-position: center 25%;
			}
			@media (max-width: $screen-xs) {
				background-position: center 50%;
			}
		}

		&--container {
			position: absolute;
			height: 100%;
			width: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
		}
	}
}
</style>
